<template>
  <div class="profile-page">
    <!-- Impersonation Banner -->
    <div v-if="showImpersonationBanner" class="alert alert-warning alert-dismissible fade show mb-3 impersonation-banner" role="alert">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <i class="bi bi-person-badge me-2"></i>
          <div>
            <strong>Impersonating:</strong> {{ impersonatedUser.name }} ({{ impersonatedUser.email }})
            <br>
            <small class="text-muted">You are viewing and can edit {{ impersonatedUser.name }}'s profile</small>
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-dark" @click="stopImpersonation" title="Return to your account">
          <i class="bi bi-arrow-left me-1"></i>Stop Impersonation
        </button>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 400px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <h4 class="alert-heading">Error Loading Profile</h4>
      <p>{{ error }}</p>
      <button class="btn btn-outline-danger" @click="loadProfile">
        <i class="bi bi-arrow-clockwise me-2"></i>Retry
      </button>
    </div>
    <!-- Profile Content -->
    <div v-else>
      <!-- Hero Section with Background -->
      <div class="profile-hero-section" style="background: #fff; border-radius: 10px; box-shadow: 0 2px 6px 0 rgba(67, 89, 113, 0.12);">
        <div class="profile-hero-background"></div>
        <div class="profile-hero-content">
          <div class="row align-items-end">
            <div class="col-auto">
              <img
                :src="userAvatar"
                alt="Profile Picture"
                class="profile-picture"
                @error="onAvatarError($event, profileUser || user)"
              >
            </div>
            <div class="col">
              <h1 class="profile-name mb-1">{{ displayName }}</h1>
              <div class="profile-meta">
                <span class="meta-item" v-if="displayProfession">
                  <i class="bi bi-briefcase me-1"></i>
                  {{ displayProfession }}
                </span>
                <span class="meta-item" v-if="displayCountry">
                  <i class="bi bi-geo-alt me-1"></i>
                  {{ displayCountry }}
                </span>
                <span class="meta-item">
                  <i class="bi bi-clock me-1"></i>
                  {{ formatDate(user?.created_at) }}
                </span>
              </div>
            </div>
            <div class="col-auto">
              <button class="btn btn-primary" @click="showEditProfileModal" style="color: #fff" :disabled="saving">
                <i class="bi bi-pencil me-2"></i>Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Main Content -->
      <div class="container-fluid mt-4" style="padding: 0">
        <div class="row">
          <!-- Left Sidebar -->
          <div class="col-md-4">
            <!-- Overview Card -->
            <div class="card mb-4">
              <div class="card-header">
                <h6 class="mb-0">OVERVIEW</h6>
              </div>
              <div class="card-body">
                <div class="overview-item">
                  <i class="bi bi-folder2 me-2"></i>
                  <span class="label">Projects:</span>
                  <span class="value">{{ profileOverview.projects }}</span>
                </div>
                <div class="overview-item">
                  <i class="bi bi-award me-2"></i>
                  <span class="label">Grants:</span>
                  <span class="value">{{ profileOverview.grants }}</span>
                </div>
                <div class="overview-item">
                  <i class="bi bi-people me-2"></i>
                  <span class="label">Memberships:</span>
                  <span class="value">{{ profileOverview.memberships }}</span>
                </div>
              </div>
            </div>
            <!-- Contact Information -->
            <div class="card mb-4">
              <div class="card-header">
                <h6 class="mb-0">PERSONAL DETAILS</h6>
              </div>
              <div class="card-body">
                <div class="row mb-3 align-items-center">
                  <div class="col-4 d-flex align-items-center">
                    <i class="bi bi-envelope me-2"></i>
                    <strong>Email:</strong>
                  </div>
                  <div class="col-8">
                    {{ displayEmail }}
                  </div>
                </div>
                <div class="row mb-3 align-items-center">
                  <div class="col-4 d-flex align-items-center">
                    <i class="bi bi-building me-2"></i>
                    <strong>Organisation:</strong>
                  </div>
                  <div class="col-8">
                    {{ displayOrganization }}
                  </div>
                </div>
                <div class="row mb-3 align-items-center">
                  <div class="col-4 d-flex align-items-center">
                    <i class="bi bi-briefcase me-2"></i>
                    <strong>Job Title:</strong>
                  </div>
                  <div class="col-8">
                    {{ displayJobTitle }}
                  </div>
                </div>
                <div class="row mb-3 align-items-center">
                  <div class="col-4 d-flex align-items-center">
                    <i class="bi bi-geo-alt me-2"></i>
                    <strong>Country:</strong>
                  </div>
                  <div class="col-8">
                    {{ displayCountry }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Working Group 1 -->
            <div class="card mb-4" v-if="displayWorkingGroup1">
              <div class="card-header">
                <h6 class="mb-0">WORKING GROUP 1</h6>
              </div>
              <div class="card-body">
                <div class="working-group-item">
                  <i class="bi bi-people-fill me-1"></i>{{ displayWorkingGroup1 }}
                </div>
              </div>
            </div>

            <!-- Working Group 2 -->
            <div class="card mb-4" v-if="displayWorkingGroup2">
              <div class="card-header">
                <h6 class="mb-0">WORKING GROUP 2</h6>
              </div>
              <div class="card-body">
                <div class="working-group-item">
                  <i class="bi bi-people-fill me-1"></i>{{ displayWorkingGroup2 }}
                </div>
              </div>
            </div>
            <!-- Related Fellows -->
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">RELATED FELLOWS</h6>
              </div>
              <div class="card-body">
                <!-- If there are fellows -->
                <div
                  v-if="relatedFellows && relatedFellows.length > 0"
                  v-for="fellow in relatedFellows"
                  :key="fellow.id"
                  class="fellow-item d-flex align-items-center mb-3"
                >
                  <img :src="fellow.avatar" alt="" class="fellow-avatar me-3 rounded-circle shadow-sm" width="45" height="45" />
                  <div class="flex-grow-1">
                    <div class="fellow-name fw-semibold">{{ fellow.name }}</div>
                    <div class="fellow-title text-muted small">{{ fellow.title }}</div>
                  </div>
<!--                  <button class="btn btn-sm btn-outline-primary">View</button>-->
                </div>

                <!-- If there are no fellows -->
                <div v-else class="text-center py-4 text-muted">
                  <i class="bi bi-people-fill fs-3 d-block mb-2 text-secondary"></i>
                  <p class="mb-0">No related fellows to display</p>
                  <small v-if="userHasNoWorkingGroup" class="text-muted">
                    Join a working group to connect with other fellows.
                  </small>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Content Area -->
          <div class="col-md-8">
            <!-- Activity Timeline -->
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <i class="bi bi-activity me-2"></i>Activity Timeline
                </h6>
                <button class="btn btn-sm btn-outline-secondary" @click="loadActivityTimeline">
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
              <div class="card-body p-0">
                <div v-if="activityTimeline.length > 0" class="activity-timeline-container">
                  <div class="timeline scrollable-timeline">
                    <div v-for="activity in activityTimeline" :key="activity.id" class="timeline-item">
                      <div class="timeline-marker"></div>
                      <div class="timeline-content">
                        <div class="d-flex align-items-start">
                          <img
                            :src="activity.avatar"
                            :alt="activity.person"
                            class="activity-avatar me-3"
                            @error="onActivityAvatarError($event, activity.person)"
                          >
                          <div class="flex-grow-1">
                            <p class="activity-text mb-1">
                              <strong>{{ activity.person }}</strong>
                              {{ activity.action }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center text-muted py-4">
                  <i class="bi bi-clock-history fs-1 mb-3 d-block"></i>
                  <p>No recent activity to display</p>
                </div>
              </div>
            </div>
            <!-- Expandable Sections -->
            <div class="expandable-sections">
              <!-- Education -->
              <div class="card mb-3" v-if="displayEducation.length">
                <div class="card-header" @click="toggleSection('education')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Education</h6>
                    <i class="bi" :class="sections.education ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.education" class="card-body">
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                      <tr class="text-primary">
                        <th>Institution</th>
                        <th>Degree</th>
                        <th>Year</th>
                        <th>Keywords</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="edu in displayEducation" :key="edu.id || edu.institution">
                        <td>{{ edu.institution || 'N/A' }}</td>
                        <td>{{ edu.degree || 'N/A' }}</td>
                        <td>{{ edu.year || 'N/A' }}</td>
                        <td>{{ edu.keywords || 'N/A' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- Professional Positions -->
              <div class="card mb-3" v-if="displayProfPositions.length">
                <div class="card-header" @click="toggleSection('positions')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Professional Positions</h6>
                    <i class="bi" :class="sections.positions ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.positions" class="card-body">
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                      <tr class="text-primary">
                        <th>Dates</th>
                        <th>Position</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="pos in displayProfPositions" :key="pos.id || pos.position">
                        <td>{{ pos.dates || 'N/A' }}</td>
                        <td>{{ pos.position || 'N/A' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- Other Experience -->
              <div class="card mb-3" v-if="displayExperience.length">
                <div class="card-header" @click="toggleSection('experience')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Other Experience</h6>
                    <i class="bi" :class="sections.experience ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.experience" class="card-body">
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                      <tr class="text-primary">
                        <th>Dates</th>
                        <th>Position</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="exp in displayExperience" :key="exp.id || exp.position">
                        <td>{{ exp.dates || 'N/A' }}</td>
                        <td>{{ exp.position || 'N/A' }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- Grants -->
              <div class="card mb-3" v-if="displayGrants.length">
                <div class="card-header" @click="toggleSection('grants')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Grants ({{ displayGrants.length }})</h6>
                    <i class="bi" :class="sections.grants ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.grants" class="card-body">
                  <div v-for="(grant, index) in displayGrants" :key="grant.id || index" class="mb-4 p-3 border rounded bg-light">
                    <div class="row">
                      <div class="col-md-4">
                        <table class="table table-borderless table-sm">
                          <tbody>
                          <tr>
                            <td><strong class="text-primary">Title:</strong></td>
                            <td>{{ grant.title || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td><strong class="text-primary">Code:</strong></td>
                            <td>{{ grant.code || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td><strong class="text-primary">Role:</strong></td>
                            <td>{{ grant.role || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td><strong class="text-primary">Amount:</strong></td>
                            <td>{{ formatCurrency(grant.amount, grant.currency) }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tbody>
                          <tr>
                            <td><strong class="text-primary">Funding Source:</strong></td>
                            <td>{{ grant.fundingSource || 'N/A' }}</td>
                          </tr>
                          <tr>
                            <td><strong class="text-primary">Period:</strong></td>
                            <td>{{ grant.startYear }}-{{ grant.endYear }}</td>
                          </tr>
                          <tr>
                            <td><strong class="text-primary">EDCTP Fellowship:</strong></td>
                            <td>{{ grant.isEdctpFellowship ? 'Yes' : 'No' }}</td>
                          </tr>
                          <tr v-if="grant.fellowshipToLink">
                            <td><strong class="text-primary">Linked Fellowship:</strong></td>
                            <td>{{ grant.fellowshipToLink }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Publications -->
              <div class="card mb-3" v-if="displayPublications.length">
                <div class="card-header" @click="toggleSection('publications')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Publications ({{ displayPublications.length }})</h6>
                    <i class="bi" :class="sections.publications ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.publications" class="card-body">
                  <div v-for="(publication, index) in displayPublications" :key="publication.id || index" class="mb-3 p-3 border rounded">
                    <h6>{{ publication.title || `Publication ${index + 1}` }}</h6>
                    <div class="row">
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tbody>
                          <tr v-if="publication.journal">
                            <td><strong class="text-primary">Journal:</strong></td>
                            <td>{{ publication.journal }}</td>
                          </tr>
                          <tr v-if="publication.date">
                            <td><strong class="text-primary">Date:</strong></td>
                            <td>{{ formatDate(publication.date) }}</td>
                          </tr>
                          <tr v-if="publication.keywords">
                            <td><strong class="text-primary">Keywords:</strong></td>
                            <td>{{ publication.keywords }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="col-md-8 publication-authors-column" v-if="publication.authors && publication.authors.length">
                        <strong class="text-primary">Authors:</strong>
                        <ul class="list-unstyled mt-1">
                          <li v-for="author in publication.authors" :key="author.name" class="small">
                            {{ author.name }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Projects -->
              <div class="card">
                <div class="card-header" @click="toggleSection('projects')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Projects ({{ displayProjects.length }})</h6>
                    <i class="bi" :class="sections.projects ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.projects" class="card-body">
                  <div v-if="displayProjects.length" class="mt-3">
                    <div v-for="(project, index) in displayProjects" :key="index" class="mb-3 p-3 border rounded">
                      <h6>{{ project.title || `Project ${index + 1}` }}</h6>
                      <div class="row">
                        <div class="col-md-6">
                          <table class="table table-borderless table-sm">
                            <tbody>
                            <tr v-if="project.startDate || project.endDate">
                              <td><strong class="text-primary">Duration:</strong></td>
                              <td>{{ project.startDate }} - {{ project.endDate }}</td>
                            </tr>
                            <tr v-if="project.year">
                              <td><strong class="text-primary">Year:</strong></td>
                              <td>{{ project.year }}</td>
                            </tr>
                            <tr v-if="project.type">
                              <td><strong class="text-primary">Type:</strong></td>
                              <td>{{ project.type }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                        <div class="col-md-6">
                          <table class="table table-borderless table-sm">
                            <tbody>
                            <tr v-if="project.code">
                              <td><strong class="text-primary">Code:</strong></td>
                              <td>{{ project.code }}</td>
                            </tr>
                            <tr v-if="project.grant">
                              <td><strong class="text-primary">Grant:</strong></td>
                              <td>{{ project.grant }}</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div v-if="project.objectives" class="mt-2">
                        <strong class="text-primary">Objectives:</strong>
                        <p class="mt-1 small">{{ project.objectives }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted text-center py-3">
                    No projects available
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Edit Profile Modal -->
    <EditProfileModal
      :show="showEditModal"
      @close="showEditModal = false"
      @save="handleSaveProfile"
      @reload-profile="reloadProfileData"
      :profile-data="transformedProfileData"
    />
    <!-- Success/Error Messages -->
    <div v-if="successMessage" class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header bg-success text-white">
          <i class="bi bi-check-circle me-2"></i>
          <strong class="me-auto">Success</strong>
          <button type="button" class="btn-close btn-close-white" @click="successMessage = null"></button>
        </div>
        <div class="toast-body">
          {{ successMessage }}
        </div>
      </div>
    </div>
    <div v-if="errorMessage" class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header bg-danger text-white">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <strong class="me-auto">Error</strong>
          <button type="button" class="btn-close btn-close-white" @click="errorMessage = null"></button>
        </div>
        <div class="toast-body">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditProfileModal from './EditProfileModal.vue'
import { mapGetters } from "vuex"
import ProfileApiService from "@/services/profile/ProfileApiService"
import { avatarMixin } from '@/mixins/avatarMixin'
import ApiService from '@/services/api';
import UserService from '@/services/users/UserService'
import AuthenticationService from '@/services/authentication/AuthenticationService'

export default {
  name: 'MyProfile',
  mixins: [avatarMixin],
  components: {
    EditProfileModal
  },
  data() {
    return {
      userHasNoWorkingGroup: false,
      showEditModal: false,
      profileData: {},
      loading: true,
      saving: false,
      error: null,
      successMessage: null,
      errorMessage: null,
      sections: {
        education: false,
        positions: false,
        experience: false,
        grants: false,
        publications: false,
        projects: false
      },
      relatedFellows: [],
      activityTimeline: [],
      workingGroupsMapping: [],
      isImpersonating: false,
      impersonatedUser: null,
      originalUser: null,
      impersonationBanner: null
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),

    countryMap() {
      return {
        DZ: 'Algeria',
        AO: 'Angola',
        BJ: 'Benin',
        BW: 'Botswana',
        BF: 'Burkina Faso',
        BI: 'Burundi',
        CM: 'Cameroon',
        CV: 'Cape Verde',
        CF: 'Central African Republic',
        TD: 'Chad',
        KM: 'Comoros',
        CG: 'Republic of the Congo',
        CD: 'Democratic Republic of the Congo',
        DJ: 'Djibouti',
        EG: 'Egypt',
        GQ: 'Equatorial Guinea',
        ER: 'Eritrea',
        SZ: 'Eswatini',
        ET: 'Ethiopia',
        GA: 'Gabon',
        GM: 'Gambia',
        GH: 'Ghana',
        GN: 'Guinea',
        GW: 'Guinea-Bissau',
        CI: 'Ivory Coast',
        KE: 'Kenya',
        LS: 'Lesotho',
        LR: 'Liberia',
        LY: 'Libya',
        MG: 'Madagascar',
        MW: 'Malawi',
        ML: 'Mali',
        MR: 'Mauritania',
        MU: 'Mauritius',
        MA: 'Morocco',
        MZ: 'Mozambique',
        NA: 'Namibia',
        NE: 'Niger',
        NG: 'Nigeria',
        RW: 'Rwanda',
        ST: 'São Tomé and Príncipe',
        SN: 'Senegal',
        SC: 'Seychelles',
        SL: 'Sierra Leone',
        SO: 'Somalia',
        ZA: 'South Africa',
        SS: 'South Sudan',
        SD: 'Sudan',
        TZ: 'Tanzania',
        TG: 'Togo',
        TN: 'Tunisia',
        UG: 'Uganda',
        ZM: 'Zambia',
        ZW: 'Zimbabwe'
      }
    },

    transformedProfileData() {
      return this.transformDataForModal(this.profileData)
    },

    userAvatar() {
      return this.getUserAvatar(this.profileUser || this.user);
    },

    profileUser() {
      return this.profileData?.user || {}
    },

    fellowDetailsData() {
      return this.profileData?.user?.fellow_details || {}
    },

    profileInfo() {
      return this.profileData?.personal || {}
    },

    displayName() {
      const name = this.profileUser?.name || 'User'  // Use profileUser, not this.user
      return this.isImpersonating ? `${name} (Impersonating)` : name
    },

    showImpersonationBanner() {
      return this.isImpersonating && this.originalUser && this.impersonatedUser
    },

    effectiveUser() {
      return this.isImpersonating ? this.impersonatedUser : this.user
    },

    displayProfession() {
      return this.fellowDetailsData?.current_job_title || ''
    },

    displayCountry() {
      const code =
        this.profileInfo?.country ||
        this.fellowDetailsData?.country ||
        ''

      if (!code) return ''

      // If already a full name, return as-is
      if (code.length > 2) return code

      return this.countryMap[code] || code
    },

    displayEmail() {
      return this.profileUser?.email || ''
    },
    displayOrganization() {
      return this.fellowDetailsData?.current_organisation || ''
    },

    displayJobTitle() {
      return this.fellowDetailsData?.current_job_title || ''
    },

    displayWorkingGroup1() {
      const workingGroupsData = this.getWorkingGroupsData()
      if (Array.isArray(workingGroupsData) && workingGroupsData.length > 0) {
        return this.getWorkingGroupDisplayName(workingGroupsData[0])
      }
      return null
    },

    displayWorkingGroup2() {
      const workingGroupsData = this.getWorkingGroupsData()
      if (Array.isArray(workingGroupsData) && workingGroupsData.length > 1) {
        return this.getWorkingGroupDisplayName(workingGroupsData[1])
      }
      return null
    },

    displayEducation() {
      const education = this.fellowDetailsData?.education
      return Array.isArray(education) ? education : []
    },

    displayProfPositions() {
      const positions = this.fellowDetailsData?.prof_pos
      if (Array.isArray(positions)) {
        return positions.map(pos => ({
          ...pos,
          dates: this.formatDateRange(pos.startDate, pos.endDate)
        }))
      }
      return []
    },

    displayExperience() {
      const experience = this.fellowDetailsData?.experience
      if (Array.isArray(experience)) {
        return experience.map(exp => ({
          ...exp,
          dates: this.formatDateRange(exp.startDate, exp.endDate)
        }))
      }
      return []
    },

    displayGrants() {
      const grants = this.fellowDetailsData?.grants
      return Array.isArray(grants) ? grants : []
    },

    displayPublications() {
      const publications = this.fellowDetailsData?.publications
      return Array.isArray(publications) ? publications : []
    },

    displayProjects() {
      const projects = this.fellowDetailsData?.current_projects
      try {
        if (typeof projects === 'string') {
          const parsed = JSON.parse(projects)
          return Array.isArray(parsed) ? parsed : []
        } else if (Array.isArray(projects)) {
          return projects
        }
      } catch (e) {
        console.warn('Error parsing projects:', e)
      }
      return []
    },

    displayMemberships() {
      const memberships = this.fellowDetailsData?.memberships
      return Array.isArray(memberships) ? memberships : []
    },

    profileOverview() {
      return {
        projects: this.displayProjects.length,
        grants: this.displayGrants.length,
        memberships: this.displayMemberships.length
      }
    }
  },

  async mounted() {
    // Check impersonation state first
    await this.checkImpersonationState()

    await this.loadWorkingGroupsMapping()
    await this.loadProfile()
    await this.loadRelatedFellows()
    await this.loadActivityTimeline()
  },

  methods: {
    clearImpersonationState() {
      this.isImpersonating = false
      this.impersonatedUser = null
      this.originalUser = null
      this.currentUser = null

      // Clear from localStorage
      localStorage.removeItem('impersonation_active')
      localStorage.removeItem('impersonation_data')

      // Clear from Vuex if applicable
      if (this.$store && this.$store.state.auth) {
        this.$store.commit('auth/SET_IMPERSONATION', {
          isImpersonating: false,
          originalUser: null,
          impersonatedUser: null
        })
      }
    },

    async reloadProfileData() {
      try {
        await this.loadProfile()
      } catch (error) {
        console.error('Error reloading profile data:', error)
      }
    },
    async checkImpersonationState() {
      try {
        const token = ApiService.getAuthToken()
        if (!token) {
          this.clearImpersonationState()
          return
        }

        const response = await UserService.getImpersonationStatus()

        let data = null
        if (response?.Data?.status === 'success') {
          data = response.Data.data
        } else if (response?.status === 'success') {
          data = response.data
        }

        if (data && data.is_impersonating) {
          this.isImpersonating = true
          this.originalUser = data.original_user
          this.impersonatedUser = data.impersonated_user
          this.currentUser = data.impersonated_user

          // Update Vuex
          if (this.$store && data.impersonated_user) {
            this.$store.commit('auth/SET_USER', data.impersonated_user)
            this.$store.commit('auth/SET_IMPERSONATION', {
              isImpersonating: true,
              originalUser: data.original_user,
              impersonatedUser: data.impersonated_user
            })
          }

          localStorage.setItem('impersonation_active', 'true')
          localStorage.setItem('impersonation_data', JSON.stringify({
            original_user: data.original_user,
            impersonated_user: data.impersonated_user
          }))
        } else {
          this.clearImpersonationState()
        }
      } catch (error) {
        console.error('Error checking impersonation state:', error)
        this.clearImpersonationState()
      }
    },

    // Working Groups Methods
    async loadWorkingGroupsMapping() {
      try {
        const ForumService = await import('@/services/forums/ForumServices')
        const response = await ForumService.default.getWorkingGroups()

        if (response?.success && Array.isArray(response.data)) {
          this.workingGroupsMapping = response.data
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          // ✅ The real list might be nested here
          this.workingGroupsMapping = response.data.data
        } else if (response?.data && Array.isArray(response.data)) {
          this.workingGroupsMapping = response.data
        } else {
          this.workingGroupsMapping = []
        }

      } catch (error) {
        console.error('Profile - Error loading working groups mapping:', error)
        this.workingGroupsMapping = []
      }
    },

    getWorkingGroupsData() {
      // Check personal data first (from the form)
      if (this.profileInfo?.workingGroups && Array.isArray(this.profileInfo.workingGroups)) {
        return this.profileInfo.workingGroups
      }
      // Check fellow details working_group (from the API)
      else if (this.fellowDetailsData?.working_group) {
        const wgString = this.fellowDetailsData.working_group
        if (typeof wgString === 'string' && wgString.trim()) {
          return wgString.split(',').map(g => g.trim()).filter(g => g)
        }
      }
      return []
    },

    getWorkingGroupDisplayName(workingGroupValue) {
      if (!workingGroupValue) return null

      const groups = Array.isArray(this.workingGroupsMapping) ? this.workingGroupsMapping : []
      if (!groups.length) {
        console.warn('⚠️ Working groups not yet loaded.')
        return 'Working Group'
      }

      const group = groups.find(g => String(g.id).trim() === String(workingGroupValue).trim())

      if (group) return group.name

      return 'Working Group'
    },

    // Avatar and Activity Methods
    onActivityAvatarError(event, userName) {
      const activity = this.activityTimeline.find(a => a.person === userName)
      if (activity && activity.originalActivity) {
        const activityUser = {
          id: activity.originalActivity.user_id,
          name: activity.originalActivity.user_name,
          avatar: activity.originalActivity.user_avatar
        }
        this.onAvatarError(event, activityUser)
      } else {
        event.target.src = `https://avatar.iran.liara.run/public/boy?username=${encodeURIComponent(userName)}`
      }
    },

    getActivityAvatar(activity) {
      const activityUser = {
        id: activity.user_id,
        name: activity.user_name,
        avatar: activity.user_avatar
      }
      return this.getUserAvatar(activityUser)
    },

    // Data Loading Methods
    async loadProfile() {
      try {
        this.loading = true
        this.error = null

        // Get the current user's profile (which will be the impersonated user if impersonating)
        const response = await ProfileApiService.getProfile()

        if (response) {
          let actualProfileData = response
          if (response.success && response.data) {
            actualProfileData = response.data
          }

          this.profileData = actualProfileData

        } else {
          this.profileData = {}
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        this.error = error.message || 'Failed to load profile data'
        this.profileData = {}

        // If it's an authentication error and we're impersonating, something went wrong
        if (error?.response?.status === 401 && this.isImpersonating) {
          this.error = 'Impersonation session may have expired. Please try refreshing the page.'
        }
      } finally {
        this.loading = false
      }
    },

    async loadRelatedFellows() {
      try {
        const response = await ProfileApiService.get('/my-profile/related-fellows')

        let fellowsData = []

        // 🧠 Handle your exact shape (Success + Data + data[])
        if (response?.Data?.success && Array.isArray(response.Data.data)) {
          fellowsData = response.Data.data
        }
        // 🧠 Common Laravel/Axios format
        else if (response?.data?.success && Array.isArray(response.data.data)) {
          fellowsData = response.data.data
        }
        // 🧠 Simplified forms
        else if (response?.success && Array.isArray(response.data)) {
          fellowsData = response.data
        } else if (Array.isArray(response?.Data)) {
          fellowsData = response.Data
        } else if (Array.isArray(response)) {
          fellowsData = response
        }

        this.relatedFellows = fellowsData
        this.userHasNoWorkingGroup = fellowsData.length === 0

      } catch (error) {
        console.error('❌ Error loading related fellows:', error)
        this.relatedFellows = []
        this.userHasNoWorkingGroup = true
      }
    },

    async loadActivityTimeline() {
      try {

        const response = await ProfileApiService.get('/my-profile/activity-timeline')

        let activities = []
        if (response && response.Data && response.Data.success && Array.isArray(response.Data.data)) {
          activities = response.Data.data
        } else if (response && response.success && Array.isArray(response.data)) {
          activities = response.data
        } else if (response && Array.isArray(response.data)) {
          activities = response.data
        } else if (Array.isArray(response)) {
          activities = response
        }

        if (activities.length > 0) {
          const limitedActivities = activities.slice(0, 10)
          this.activityTimeline = limitedActivities.map(activity => ({
            id: activity.id,
            person: activity.user_name,
            action: activity.message,
            avatar: this.getActivityAvatar(activity),
            time: activity.human_time,
            originalActivity: activity
          }))
        } else {
          this.activityTimeline = []
        }
      } catch (error) {
        console.error('Error loading activity timeline:', error)
        this.activityTimeline = []

        // Don't show error for activity timeline failures unless it's a critical auth issue
        if (error?.response?.status === 401 && this.isImpersonating) {
          this.showErrorMessage('Session expired. Please refresh the page.')
        }
      }
    },

    // Profile Management Methods
    async handleSaveProfile(updatedData) {
      try {
        this.saving = true
        this.errorMessage = null

        console.log('DEBUG - Profile save payload (publications):', updatedData.publications);


        // Validate the data
        const validation = ProfileApiService.validateProfileData(updatedData)
        if (!validation.isValid) {
          this.showErrorMessage(validation.errors.join(', '))
          return
        }

        // Add timestamp for tracking
        if (updatedData.personal) {
          updatedData.personal._updateTimestamp = Date.now()
        }

        // Save the profile (the API will automatically use the current authenticated user context)
        const response = await ProfileApiService.updateProfile(updatedData)

        if (response) {
          this.showSuccessMessage(
            this.isImpersonating
              ? `Profile updated successfully for ${this.impersonatedUser?.name}!`
              : 'Profile updated successfully!'
          )
          this.showEditModal = false

          // Reload the profile data
          await this.loadProfile()

          // Refresh activity timeline after a delay
          setTimeout(() => {
            this.loadActivityTimeline()
          }, 2000)
        } else {
          throw new Error('Failed to update profile')
        }
      } catch (error) {
        console.error('Error saving profile:', error)

        let errorMessage = error.message || 'Failed to update profile'

        // Handle specific impersonation-related errors
        if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to edit this profile.'
        } else if (error?.response?.status === 401 && this.isImpersonating) {
          errorMessage = 'Impersonation session expired. Please refresh and try again.'
        }

        this.showErrorMessage(errorMessage)
      } finally {
        this.saving = false
      }
    },

    async refreshAllData() {
      try {
        await this.checkImpersonationState()
        await this.loadProfile()
        await this.loadRelatedFellows()
        await this.loadActivityTimeline()

      } catch (error) {
        console.error('Error refreshing data:', error)
      }
    },

    async stopImpersonation() {
      try {
        const UserService = await import('@/services/users/UserService')
        const response = await UserService.default.stopImpersonation()

        if (response?.Data?.status === 'success' || response?.status === 'success') {
          // Clear impersonation state
          this.isImpersonating = false
          this.impersonatedUser = null
          this.originalUser = null

          const restoredToken = response?.Data?.data?.access_token || response?.data?.access_token

          if (!restoredToken) {
            throw new Error('No access token received for original user')
          }

          AuthenticationService.setToken(restoredToken)

          // Update Vuex state back to original user
          const originalUserData = response?.Data?.data?.original_user || response?.data?.original_user
          if (originalUserData && this.$store.state.auth) {
            AuthenticationService.setUserData(originalUserData)
            this.$store.commit('auth/SET_USER', originalUserData)
            this.$store.commit('auth/SET_TOKEN', AuthenticationService.getJwt())
            this.$store.commit('auth/SET_IMPERSONATION', {
              isImpersonating: false,
              originalUser: null,
              impersonatedUser: null
            })
          }

          this.showSuccessMessage(`Impersonation stopped. Welcome back, ${originalUserData?.name}!`)

          // Redirect to own dashboard without a full page reload
          setTimeout(() => {
            this.$router.push('/my-profile')
          }, 1500)
        }
      } catch (error) {
        console.error('Error stopping impersonation:', error)
        this.showErrorMessage('Failed to stop impersonation. Please try refreshing the page.')
      }
    },

    // Utility Methods
    toggleSection(section) {
      this.sections[section] = !this.sections[section]
    },

    formatDate(dateString) {
      return ProfileApiService.formatDate(dateString)
    },

    formatDateRange(startDate, endDate) {
      const start = startDate ? this.formatDate(startDate) : ''
      const end = endDate ? this.formatDate(endDate) : 'Present'

      if (start && end) {
        return `${start} - ${end}`
      } else if (start) {
        return `${start} - Present`
      } else if (end) {
        return `Until ${end}`
      }
      return 'N/A'
    },

    formatCurrency(amount, currency) {
      return ProfileApiService.formatCurrency(amount, currency)
    },

    showSuccessMessage(message) {
      this.successMessage = message
      setTimeout(() => {
        this.successMessage = null
      }, 5000)
    },

    showErrorMessage(message) {
      this.errorMessage = message
      setTimeout(() => {
        this.errorMessage = null
      }, 8000)
    },

    transformDataForModal(profileData) {
      if (!profileData) {
        return null
      }
      return {
        success: true,
        data: profileData
      }
    },

    showEditProfileModal() {
      this.showEditModal = true
    }
  },

  watch: {
    user: {
      handler(newUser, oldUser) {
        // If the user changes and we have a new valid user, reload profile
        if (newUser && newUser.id && newUser.id !== oldUser?.id) {
          this.loadProfile()
        }
      },
      immediate: false // Don't run immediately since we handle this in mounted()
    },

    // Watch for impersonation state changes
    isImpersonating: {
      handler(newState, oldState) {
        if (newState !== oldState) {
          console.log('Impersonation state changed:', { newState, oldState })
        }
      }
    }
  },

  beforeUnmount() {
    this.successMessage = null
    this.errorMessage = null
  }
}
</script>

<style scoped>
/* Add these styles to your existing <style> section */

.impersonation-banner {
  border-left: 4px solid #ffc107;
  background-color: #fff3cd;
  border-color: #ffeaa7;
  position: sticky;
  top: 0;
  z-index: 1000;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.impersonation-banner .alert-heading {
  color: #856404;
}

.impersonation-banner i.bi-person-badge {
  font-size: 1.2rem;
  color: #856404;
}

.impersonation-banner .btn-outline-dark {
  border-color: #856404;
  color: #856404;
  background-color: transparent;
}

.impersonation-banner .btn-outline-dark:hover {
  background-color: #856404;
  color: #fff;
}

/* Enhance profile name when impersonating */
.profile-name.impersonating {
  color: #856404;
}

.profile-name.impersonating::after {
  content: " (Impersonating)";
  font-size: 0.8rem;
  font-weight: normal;
  color: #6c757d;
  font-style: italic;
}

/* Add visual indicator to edit button when impersonating */
.btn-primary.impersonating {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.btn-primary.impersonating:hover {
  background-color: #e0a800;
  border-color: #d39e00;
  color: #212529;
}

.profile-page {
  margin: -1.5rem -1.5rem 0 -1.5rem;
}

.profile-hero-section {
  position: relative;
  padding: 2rem 0;
  margin-bottom: 0;
}

.profile-hero-background {
  border-radius: 10px 10px 0 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 250px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-image: url('/public/bg-header.png');
  background-size: cover;
  background-position: center;
}

.profile-hero-content {
  position: relative;
  z-index: 2;
  padding: 2rem 1.5rem;
  margin-top: 150px;
}

.profile-picture {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.profile-name {
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.profile-meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.meta-item {
  color: #6c757d;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.overview-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f3f4;
}

.overview-item:last-child {
  border-bottom: none;
}

.overview-item .label {
  margin-left: 0.5rem;
  color: #6c757d;
}

.overview-item .value {
  margin-left: auto;
  font-weight: 600;
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.875rem;
}

.working-group-item {
  padding: 0.5rem 0.75rem;
  color: #fff;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #009CAD 0%, #00BCCF 100%);
  margin: 0.25rem 0;
  border-radius: 6px;
  border-left: 3px solid #009CAD;
}

.fellow-item {
  padding: 0.5rem 0;
}

.fellow-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.fellow-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.fellow-title {
  font-size: 0.8rem;
  color: #6c757d;
}

.activity-timeline-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 1rem;
}

.scrollable-timeline {
  position: relative;
}

.timeline-item {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.5rem;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 12px;
  height: 12px;
  background: #28a745;
  border-radius: 50%;
  border: 3px solid #e9ecef;
  z-index: 2;
}

.timeline-marker::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 12px;
  width: 2px;
  height: calc(100% + 1.5rem);
  background: #e9ecef;
  z-index: -1;
}

.timeline-item:last-child .timeline-marker::before {
  display: none;
}

.activity-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.activity-text {
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 0.9rem;
  line-height: 1.4;
}

.card {
  border: 1px solid #e9ecef;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.75rem 1rem;
}

.card-header h6 {
  color: #6c757d;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.expandable-sections .card-header:hover {
  background-color: #e9ecef;
}

.table th {
  font-weight: 600;
  padding: 0.5rem;
  border: none;
}

.table td {
  padding: 0.5rem;
  border: none;
  color: #495057;
}

.table-sm th, .table-sm td {
  padding: 0.3rem 0.5rem;
}

.publication-authors-column {
  word-break: break-word;
}

.toast-container .toast {
  min-width: 300px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Scrollbar Styling */
.activity-timeline-container::-webkit-scrollbar {
  width: 6px;
}

.activity-timeline-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.activity-timeline-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.activity-timeline-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.activity-timeline-container {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

@media (max-width: 768px) {
  .profile-meta {
    flex-direction: column;
    gap: 0.5rem;
  }

  .profile-hero-content {
    padding: 1rem;
    margin-top: 50px;
  }

  .profile-name {
    font-size: 1.5rem;
  }
}

.fellow-item:hover {
  background-color: #f8f9fa;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.fellow-avatar {
  object-fit: cover;
}

.card-header {
  background-color: #f7f9fc;
  border-bottom: 1px solid #dee2e6;
}

.text-muted small {
  display: block;
  margin-top: 4px;
}

</style>
